import React from "react";
import { Link } from "react-router-dom";
import { productImage, hasCover } from "../lib/brand";
import { useCart } from "../lib/cart";
import { Check, Plus } from "lucide-react";
import ProtectedImage from "./ProtectedImage";

/**
 * ArtCard - Product preview tile.
 *
 * Anti-theft strategy: watermark is intentionally NOT visible by default to
 * preserve a clean gallery aesthetic. It fades in only when a viewer attempts
 * to right-click / drag the image (handled by ProtectedImage).
 */
export default function ArtCard({ product, large = false }) {
  const { add, has } = useCart();
  const img = productImage(product);
  const inCart = has(product.id);

  return (
    <div
      data-testid={`art-card-${product.id}`}
      className="group relative bg-[#09120C] border border-[#1A2B20] overflow-hidden flex flex-col"
    >
      <Link to={`/product/${product.id}`} className="block">
        <ProtectedImage
          src={img}
          alt={product.title}
          aspectClass={large ? "aspect-[4/5]" : "aspect-square"}
          imgClassName={
            hasCover(product)
              ? "transition-transform duration-[1400ms] group-hover:scale-110"
              : "!object-contain p-12 opacity-70 transition-transform duration-[1400ms] group-hover:scale-105"
          }
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-50 group-hover:opacity-80 transition-opacity pointer-events-none" />
        </ProtectedImage>
      </Link>

      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#5C7064]">{product.type}</div>
            <Link to={`/product/${product.id}`} className="block mt-1">
              <h3 data-testid={`art-title-${product.id}`} className="font-serif-display text-xl text-white leading-tight hover:text-[#279153] transition-colors">
                {product.title}
              </h3>
            </Link>
          </div>
          <div className="text-right">
            <div className="text-xs text-[#5C7064] tracking-wide">USD</div>
            <div data-testid={`art-price-${product.id}`} className="font-serif-display text-xl text-white">${Number(product.price).toFixed(2)}</div>
          </div>
        </div>

        <button
          data-testid={`add-to-cart-${product.id}`}
          disabled={inCart}
          onClick={() => add(product)}
          className={`mt-2 flex items-center justify-center gap-2 px-4 py-3 text-[11px] tracking-[0.3em] uppercase transition-all
            ${inCart
              ? "bg-[#1A2B20] text-[#95A89C] cursor-not-allowed"
              : "bg-[#1E7040] text-white hover:bg-[#165932]"}`}
        >
          {inCart ? <><Check className="w-4 h-4" /> In cart</> : <><Plus className="w-4 h-4" /> Add to cart</>}
        </button>
      </div>
    </div>
  );
}
